/**
 * The backend expects `status` as a single comma-separated string (e.g. `?status=funded,reviewed`),
 * not axios's default array serialization (`status[]=funded&status[]=reviewed`).
 * When `status` is an empty array, the param is omitted entirely so no status filter is applied.
 *
 * @param {Object} params
 * @returns {Object}
 */
export function serializeStatusParam(params) {
  if (!Array.isArray(params.status)) {
    return params;
  }

  const { status, ...rest } = params;

  if (status.length === 0) {
    return rest;
  }

  return { ...rest, status: status.join(',') };
}

/**
 * Drops params whose value is an empty string, so unset filters (e.g. a country/currency
 * filter left blank) are omitted from the request instead of being sent as `key=`.
 *
 * @param {Object} params
 * @returns {Object}
 */
export function omitEmptyParams(params) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== ''));
}

/**
 * `date` only selects which date field a range filters on - it's a no-op without
 * `date_from`/`date_to` set, so drop it when no range has been chosen yet.
 *
 * @param {Object} params
 * @returns {Object}
 */
export function omitDateFieldWithoutRange(params) {
  if (!('date' in params) || params.date_from || params.date_to) {
    return params;
  }

  const rest = { ...params };
  delete rest.date;
  return rest;
}

/**
 * Applies all the request-side param cleanup a list query needs before hitting the API:
 * comma-joins `status`, drops the `date` field selector when no range is set, then drops
 * any remaining empty-string filters.
 *
 * @param {Object} params
 * @returns {Object}
 */
export function sanitizeListParams(params) {
  return omitEmptyParams(omitDateFieldWithoutRange(serializeStatusParam(params)));
}
